/**
 * 销售对练's start page. Practice is set up in a 知车 AI conversation with
 * the coach: the prompt and every case here open one.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { CoachingNavParams } from '@euler/app/flows/coaching';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { ClipboardList } from 'lucide-react-native';
import { memo, useCallback, useRef } from 'react';
import { TouchableOpacity } from 'react-native';
import {
  openCoachingCase,
  openCoachingSetup,
} from '../../functions/openCoachingSetup';
import { useCoachingHome } from '../../functions/useCoachingHome';
import { useCoachingStreak } from '../../progress/functions/useCoachingStreak';
import { CoachingHomeView } from './CoachingHomeView';

export const CoachingHomeScreen = wrapNavigatorScreen(
  () => {
    const appNavigation = useAppNavigation();
    const navigation = useNavigation<StackNavigationProp<CoachingNavParams>>();
    const profile = useUserProfileInfo();
    const { suggestions, catalogue, failed, reload } = useCoachingHome();
    const { streak, reload: reloadStreak } = useCoachingStreak();
    // Back from a practice: today may now count.
    const focused = useRef(false);
    useFocusEffect(
      useCallback(() => {
        if (focused.current) reloadStreak();
        focused.current = true;
      }, [reloadStreak]),
    );
    return (
      <CoachingHomeView
        name={profile?.user.name ?? null}
        streak={streak}
        suggestions={suggestions}
        catalogue={catalogue}
        failed={failed}
        onRetry={reload}
        onDescribe={text => openCoachingSetup(appNavigation, text)}
        onPractise={card =>
          openCoachingCase(appNavigation, {
            caseId: card.id,
            title: card.title,
          })
        }
        onBrowse={() => navigation.navigate('catalog', {})}
        onOpenCollection={collection =>
          navigation.push('collection', { slug: collection.slug })
        }
      />
    );
  },
  {
    title: '销售对练',
    headerRight: () => <HistoryButton />,
  },
);

/** 我的练习: the trainee's started practice. */
const HistoryButton = memo(() => {
  const { rpx } = useResponsiveContext();
  const navigation = useNavigation<StackNavigationProp<CoachingNavParams>>();
  return (
    <TouchableOpacity
      onPress={() => navigation.navigate('history', {})}
      hitSlop={PredefinedHitSlops.large}
      accessibilityRole="button"
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: rpx(4),
        paddingRight: rpx(16),
        paddingLeft: rpx(12),
      }}
    >
      <ClipboardList size={rpx(16)} color={aiColors.primary} />
      <Typography.Text size={rpx(14)} color={aiColors.primary}>
        我的练习
      </Typography.Text>
    </TouchableOpacity>
  );
});
